import React, { useState, useEffect } from 'react';
import {
  ClipboardCheck,
  Play,
  FileText,
  BarChart3,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Target,
  Award,
  Loader2,
  RefreshCw,
  Download,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  Zap
} from 'lucide-react';
import { useToast } from '../components/Toast';
import { getApiBaseUrl } from '../utils/api';
import CompanyDataAuthorizationPanel from '../components/CompanyDataAuthorizationPanel';
import {
  isAuthorizationRequiredError,
  readApiResponseError,
} from '../services/companyDataAuthorizationService';

interface EvaluationCriteria {
  category: string;
  name: string;
  score: number;
  maxScore: number;
  weight: number;
  feedback: string;
  status: 'excellent' | 'good' | 'average' | 'poor';
  suggestions: string[];
}

interface EvaluationResult {
  overallScore: number;
  maxScore: number;
  grade: string;
  criteria: EvaluationCriteria[];
  strengths: string[];
  weaknesses: string[];
  recommendations: string[];
}

interface Proposal {
  id: number | string;
  title: string;
  status: string;
  company_id: string;
  created_at?: string;
}

const VirtualEvaluation: React.FC = () => {
  const toast = useToast();
  const progressIntervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  const [selectedProposal, setSelectedProposal] = useState<number | string | null>(null);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [evaluationProgress, setEvaluationProgress] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [expandedCriteria, setExpandedCriteria] = useState<number | string | null>(null);

  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [proposalsLoading, setProposalsLoading] = useState(true);
  const [evaluationResult, setEvaluationResult] = useState<EvaluationResult | null>(null);
  const [authorizationId, setAuthorizationId] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const selectedProposalData =
    proposals.find((proposal) => proposal.id === selectedProposal) || null;
  const selectedCompanyId = selectedProposalData?.company_id || null;

  const invalidateAuthorizationIfNeeded = (
    apiError: { status: number; code?: string; message: string } | null,
  ): boolean => {
    const invalid =
      apiError !== null && isAuthorizationRequiredError(apiError);
    if (invalid) setAuthorizationId(null);
    return invalid;
  };

  // mockEvaluationResult 제거 - 실 AI 평가만 사용

  useEffect(() => {
    const controller = new AbortController();

    const fetchProposals = async () => {
      setProposalsLoading(true);
      try {
        const token = localStorage.getItem('proposalcraft_access_token');
        const baseUrl = getApiBaseUrl();

        const response = await fetch(`${baseUrl}/api/proposals?limit=50`, {
          headers: { 'Authorization': `Bearer ${token}` },
          signal: controller.signal,
        });

        if (response.ok) {
          const data = await response.json();
          // 백엔드는 ProposalResponse 배열을 직접 반환
          const items = Array.isArray(data) ? data : (data.items || data.proposals || []);
          setProposals(items.map((p: any) => ({
            id: p.id || p.proposal_id,
            title: p.title,
            status: p.status || 'draft',
            company_id: p.company_id,
            created_at: p.created_at,
          })));
        }
      } catch (err: any) {
        if (err.name === 'AbortError') return;
        console.error('Failed to fetch proposals:', err);
      } finally {
        setProposalsLoading(false);
      }
    };

    fetchProposals();
    return () => controller.abort();
  }, []);

  const handleEvaluate = async () => {
    if (!selectedProposal) return;
    if (!selectedCompanyId || !authorizationId) {
      toast.error(
        '평가 전에 해당 기업의 자료 처리 동의를 선택하거나 등록해 주세요.',
      );
      return;
    }

    setIsEvaluating(true);
    setEvaluationProgress(0);

    try {
      // 진행률 시뮬레이션 (백그라운드)
      progressIntervalRef.current = setInterval(() => {
        setEvaluationProgress(prev => Math.min(prev + 10, 90));
      }, 500);

      const token = localStorage.getItem('proposalcraft_access_token');
      const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? 'http://localhost:8001' : `${window.location.protocol}//${window.location.hostname}:8001`;

      const response = await fetch(`${baseUrl}/api/proposals/evaluate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          proposal_id: selectedProposal,
          company_id: selectedCompanyId,
          authorization_id: authorizationId,
          save_result: true,
        }),
      });

      if (progressIntervalRef.current) { clearInterval(progressIntervalRef.current); progressIntervalRef.current = null; }

      if (response.ok) {
        const data = await response.json();

        // 백엔드 응답을 프론트엔드 형식으로 변환
        const result: EvaluationResult = {
          overallScore: data.total_score || data.evaluation?.total_score || 0,
          maxScore: data.max_score || 100,
          grade: data.grade || data.evaluation?.grade || 'C',
          criteria: (data.criteria_scores || data.evaluation?.criteria_scores || []).map((c: any) => ({
            category: c.category || '',
            name: c.item || c.name || '',
            score: c.score || 0,
            maxScore: c.max_score || 10,
            weight: c.weight || 1.0,
            feedback: c.feedback || '',
            status: c.score / c.max_score >= 0.8 ? 'excellent' :
                    c.score / c.max_score >= 0.7 ? 'good' :
                    c.score / c.max_score >= 0.5 ? 'average' : 'poor',
            suggestions: c.suggestions || [],
          })),
          strengths: data.strengths || data.evaluation?.strengths || [],
          weaknesses: data.weaknesses || data.evaluation?.weaknesses || [],
          recommendations: data.recommendations || data.evaluation?.recommendations || [],
        };

        setEvaluationResult(result);
        setEvaluationProgress(100);
        setShowResult(true);
      } else {
        throw await readApiResponseError(
          response,
          'AI 평가에 실패했습니다.',
        );
      }
    } catch (caught: unknown) {
      if (progressIntervalRef.current) { clearInterval(progressIntervalRef.current); progressIntervalRef.current = null; }
      setEvaluationProgress(0);
      const apiError =
        typeof caught === 'object' &&
        caught !== null &&
        'status' in caught &&
        'message' in caught
          ? caught as {
              status: number;
              code?: string;
              message: string;
            }
          : null;
      toast.error(
        invalidateAuthorizationIfNeeded(apiError)
          ? '기업 자료 처리 동의가 필요하거나 만료되었습니다. 동의를 다시 확인해 주세요.'
          : apiError?.message ||
              'AI 평가에 실패했습니다. 서버 연결을 확인하고 다시 시도해주세요.',
      );
    } finally {
      setIsEvaluating(false);
    }
  };

  const handleDownloadReport = async () => {
    if (
      !evaluationResult ||
      !selectedCompanyId ||
      !authorizationId
    ) {
      toast.error(
        '평가 결과를 내보내려면 기업 자료 처리 동의를 선택해 주세요.',
      );
      return;
    }

    const report = `# 제안서 가상 평가 보고서\n\n` +
      `## 종합 점수: ${evaluationResult.overallScore}/${evaluationResult.maxScore} (${evaluationResult.grade})\n\n` +
      `## 평가 항목별 점수\n` +
      evaluationResult.criteria.map(c => `- ${c.category}/${c.name}: ${c.score}/${c.maxScore} - ${c.feedback}`).join('\n') +
      `\n\n## 강점\n` + evaluationResult.strengths.map(s => `- ${s}`).join('\n') +
      `\n\n## 약점\n` + evaluationResult.weaknesses.map(w => `- ${w}`).join('\n') +
      `\n\n## 개선 권고\n` + evaluationResult.recommendations.map(r => `- ${r}`).join('\n');

    setIsDownloading(true);
    try {
      const token = localStorage.getItem('proposalcraft_access_token');
      const response = await fetch(
        `${getApiBaseUrl()}/api/proposals/export/document`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            proposal_data: {
              title: '제안서 가상 평가 보고서',
              content: report,
              company_name: '',
              sections: [
                '종합 점수',
                '평가 항목별 점수',
                '강점',
                '약점',
                '개선 권고',
              ],
            },
            company_id: selectedCompanyId,
            authorization_id: authorizationId,
            format: 'markdown',
          }),
        },
      );
      if (!response.ok) {
        throw await readApiResponseError(
          response,
          '평가 결과 내보내기에 실패했습니다.',
        );
      }

      const data = await response.json();
      const blob = data.is_base64
        ? new Blob(
            [
              Uint8Array.from(atob(data.content), (character) =>
                character.charCodeAt(0),
              ),
            ],
            { type: data.content_type || 'text/markdown;charset=utf-8' },
          )
        : new Blob([data.content], {
            type: data.content_type || 'text/markdown;charset=utf-8',
          });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download =
        data.filename ||
        `evaluation_report_${new Date().toISOString().split('T')[0]}.md`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    } catch (caught: unknown) {
      const apiError =
        typeof caught === 'object' &&
        caught !== null &&
        'status' in caught &&
        'message' in caught
          ? caught as {
              status: number;
              code?: string;
              message: string;
            }
          : null;
      toast.error(
        invalidateAuthorizationIfNeeded(apiError)
          ? '기업 자료 처리 동의가 필요하거나 만료되었습니다. 동의를 다시 확인해 주세요.'
          : apiError?.message || '평가 결과 내보내기에 실패했습니다.',
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const handleAutoImprove = async () => {
    if (!evaluationResult || !selectedProposal) return;
    toast.info('AI 자동 개선 기능은 준비 중입니다. 약점 및 개선 권고를 참고하여 수동으로 개선해 주세요.');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'excellent': return 'text-green-600 bg-green-100';
      case 'good': return 'text-blue-600 bg-blue-100';
      case 'average': return 'text-yellow-600 bg-yellow-100';
      case 'poor': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getGradeColor = (grade: string) => {
    switch (grade) {
      case 'A': return 'text-green-600 bg-green-100 border-green-200';
      case 'B': return 'text-blue-600 bg-blue-100 border-blue-200';
      case 'C': return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      case 'D': return 'text-orange-600 bg-orange-100 border-orange-200';
      case 'F': return 'text-red-600 bg-red-100 border-red-200';
      default: return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getScoreBarColor = (score: number, maxScore: number) => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 90) return 'bg-green-500';
    if (percentage >= 75) return 'bg-blue-500';
    if (percentage >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            가상 평가 시스템
          </h1>
          <p className="text-gray-600 mt-2">AI 기반 사전 평가로 제안서 품질을 점검합니다</p>
        </div>
      </div>

      {!showResult ? (
        <>
          {/* 제안서 선택 */}
          <div className="card">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">평가할 제안서 선택</h2>
            {proposalsLoading && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-6 w-6 text-emerald-600 animate-spin" />
                <span className="ml-2 text-gray-600">제안서 목록 로딩 중...</span>
              </div>
            )}
            {!proposalsLoading && proposals.length === 0 && (
              <div className="text-center py-6 text-gray-500">
                <FileText className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                <p>작성된 제안서가 없습니다. 먼저 제안서를 작성해주세요.</p>
              </div>
            )}
            <div className="space-y-3">
              {proposals.map((proposal) => (
                <div
                  key={proposal.id}
                  onClick={() => setSelectedProposal(proposal.id)}
                  className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    selectedProposal === proposal.id
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center text-white">
                        <FileText className="h-6 w-6" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{proposal.title}</h3>
                        <p className="text-sm text-gray-500">{proposal.created_at ? new Date(proposal.created_at).toLocaleDateString('ko-KR') : ''}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                        proposal.status === '작성완료' || proposal.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {proposal.status}
                      </span>
                      {selectedProposal === proposal.id && (
                        <CheckCircle className="h-6 w-6 text-emerald-600" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {selectedCompanyId && (
            <CompanyDataAuthorizationPanel
              companyId={selectedCompanyId}
              value={authorizationId}
              onChange={setAuthorizationId}
            />
          )}

          {/* 평가 시작 */}
          <div className="card">
            <div className="text-center py-8">
              {isEvaluating ? (
                <div className="space-y-4">
                  <Loader2 className="h-16 w-16 text-emerald-600 mx-auto animate-spin" />
                  <h3 className="text-xl font-semibold text-gray-900">AI가 제안서를 평가하고 있습니다...</h3>
                  <p className="text-gray-600">평가 기준에 따라 상세 분석 중입니다</p>
                  <div className="max-w-md mx-auto">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-gray-600">평가 진행률</span>
                      <span className="font-medium text-emerald-600">{evaluationProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div 
                        className="bg-emerald-600 h-3 rounded-full transition-all duration-300"
                        style={{ width: `${evaluationProgress}%` }}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="w-20 h-20 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-2xl mx-auto flex items-center justify-center">
                    <ClipboardCheck className="h-10 w-10 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    가상 평가 실행
                  </h3>
                  <p className="text-gray-600 max-w-md mx-auto">
                    실제 심사 기준을 적용하여 AI가 제안서를 사전 평가합니다.<br />
                    강점, 약점, 개선 권고사항을 상세히 분석합니다.
                  </p>
                  <button
                    onClick={handleEvaluate}
                    disabled={!selectedProposal || !authorizationId}
                    className="px-8 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Play className="h-5 w-5 mr-2 inline" />
                    평가 시작
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      ) : evaluationResult && (
        <>
          {/* 평가 결과 요약 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="card text-center">
              <div className={`w-16 h-16 mx-auto mb-3 rounded-2xl flex items-center justify-center border-2 ${getGradeColor(evaluationResult.grade)}`}>
                <span className="text-3xl font-bold">{evaluationResult.grade}</span>
              </div>
              <p className="text-sm text-gray-500">종합 등급</p>
            </div>
            <div className="card text-center">
              <div className="text-3xl font-bold text-gray-900 mb-2">
                {evaluationResult.overallScore}<span className="text-lg text-gray-400">/{evaluationResult.maxScore}</span>
              </div>
              <p className="text-sm text-gray-500">총점</p>
            </div>
            <div className="card text-center">
              <div className="text-3xl font-bold text-emerald-600 mb-2">
                {evaluationResult.maxScore > 0 ? Math.round((evaluationResult.overallScore / evaluationResult.maxScore) * 100) : 0}%
              </div>
              <p className="text-sm text-gray-500">득점률</p>
            </div>
            <div className="card text-center">
              <div className="text-3xl font-bold text-blue-600 mb-2">{evaluationResult.strengths.length}</div>
              <p className="text-sm text-gray-500">강점 항목</p>
            </div>
          </div>

          {/* 상세 평가 결과 */}
          <div className="grid grid-cols-2 gap-6">
            {/* 항목별 점수 */}
            <div className="card">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">항목별 평가</h2>
              <div className="space-y-3">
                {evaluationResult.criteria.map((criterion, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div
                      onClick={() => setExpandedCriteria(expandedCriteria === index ? null : index)}
                      className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50"
                    >
                      <div className="flex items-center space-x-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(criterion.status)}`}>
                          {criterion.status === 'excellent' ? '우수' :
                           criterion.status === 'good' ? '양호' :
                           criterion.status === 'average' ? '보통' : '미흡'}
                        </span>
                        <div>
                          <p className="font-medium text-gray-900">{criterion.name}</p>
                          <p className="text-xs text-gray-500">{criterion.category}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="text-right">
                          <span className="font-bold text-gray-900">{criterion.score}</span>
                          <span className="text-gray-400">/{criterion.maxScore}</span>
                        </div>
                        {expandedCriteria === index ? (
                          <ChevronUp className="h-4 w-4 text-gray-400" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </div>
                    {expandedCriteria === index && (
                      <div className="px-3 pb-3">
                        <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                          <div 
                            className={`h-2 rounded-full ${getScoreBarColor(criterion.score, criterion.maxScore)}`}
                            style={{ width: `${(criterion.score / criterion.maxScore) * 100}%` }}
                          />
                        </div>
                        <p className="text-sm text-gray-600">{criterion.feedback}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* 강점/약점/권고사항 */}
            <div className="space-y-6">
              {/* 강점 */}
              <div className="card">
                <div className="flex items-center space-x-2 mb-4">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                  <h3 className="text-lg font-semibold text-gray-900">강점</h3>
                </div>
                <ul className="space-y-2">
                  {evaluationResult.strengths.map((strength, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{strength}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* 약점 */}
              <div className="card">
                <div className="flex items-center space-x-2 mb-4">
                  <TrendingDown className="h-5 w-5 text-red-600" />
                  <h3 className="text-lg font-semibold text-gray-900">약점</h3>
                </div>
                <ul className="space-y-2">
                  {evaluationResult.weaknesses.map((weakness, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{weakness}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* 개선 권고사항 */}
              <div className="card bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200">
                <div className="flex items-center space-x-2 mb-4">
                  <Lightbulb className="h-5 w-5 text-amber-600" />
                  <h3 className="text-lg font-semibold text-gray-900">개선 권고사항</h3>
                </div>
                <ul className="space-y-2">
                  {evaluationResult.recommendations.map((rec, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <span className="w-5 h-5 bg-amber-200 text-amber-800 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0">
                        {index + 1}
                      </span>
                      <span className="text-sm text-gray-700">{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* 액션 버튼 */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => {
                setShowResult(false);
                setSelectedProposal(null);
                setEvaluationResult(null);
              }}
              className="flex items-center px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <RefreshCw className="h-5 w-5 mr-2" />
              다시 평가
            </button>
            <div className="flex space-x-3">
              <button
                onClick={handleDownloadReport}
                disabled={!authorizationId || isDownloading}
                className="flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isDownloading ? (
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                ) : (
                  <Download className="h-5 w-5 mr-2" />
                )}
                {isDownloading ? '내보내는 중...' : '평가 결과 다운로드'}
              </button>
              <button
                onClick={handleAutoImprove}
                disabled
                className="flex items-center px-4 py-2 bg-gray-400 text-white rounded-lg cursor-not-allowed opacity-60"
                title="AI 자동 개선 기능은 준비 중입니다"
              >
                <Zap className="h-5 w-5 mr-2" />
                AI 자동 개선 (준비중)
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default VirtualEvaluation;

